/**
 * **서명행 축의 대기(미해소) 판정** — 제출 후 화면이 공유하는 한 벌.
 *
 * ## 왜 서명행인가
 * 같은 질문("이 칸이 대기 중인가")에 소스가 둘 있다. 제출 **전**에는 결재란 정의
 * (`approvalLineDefinition`)가 정본이고, 제출 **후**에는 `ApprovalSignature` 행이 정본이다.
 * 둘은 제출 전까지 같아서 갈라진다는 사실이 잘 안 보이는데, 자동 재개는 **서명행만** 채우고
 * 정의는 일부러 그대로 둔다(S76-A: 완료 시점 스냅샷에만 사람으로 굳힌다). 그래서 재개 뒤에도
 * 정의로 판정하면 화면이 영영 "대기 중"이라고 말한다 — 2026-08-27 dev 실측에서 실제로 그랬다.
 *
 * ## 왜 status로 가르는가
 * 서명행엔 `assignmentChain`이 없어 *미지정(종이 결재)* 과 *미해소(대기)* 를 체인으로 가를 수
 * 없다. 대신 **`status`가 가른다**: 미지정 칸은 무서명 `APPROVED`로 만들어지고(S8·S67),
 * 대기 칸만 `PENDING`인 채 담당자가 없다.
 *
 * ## 서버 판정과의 관계 — **같은 축이 아니다**
 * 서버 `isParkedApprovalSlot`(`apps/server/src/reports/utils/approval-slot-assignment.util.ts`)은
 * **신원 FK 부재만** 본다(`memberId` 없음 — 구 `approverProfileId` 축은 2026-09-16 E4에서 컬럼째
 * 삭제됐다). 그 자리에선 그것으로 충분하다 — 묻는 것이 "이 칸에 결재 토큰을 발급해도 되나"이고,
 * 연락처 해소가 FK 없이는 어차피 null이기 때문이다.
 *
 * 화면은 **세 축을 모두** 본다: `status === 'PENDING'` + 신원 FK 부재 + `signerName`이 서버
 * 플레이스홀더와 일치. 축이 더 좁은 이유는 아래 함수 주석에 있다 — FK 부재만 보면 성명 축으로
 * 신원이 해소되는 **레거시 행**을 "담당자 지정 필요"로 오탐한다(dev 실측 6건). 서버는 그 행에
 * 토큰을 안 주면 그만이지만, 화면은 정상 결재에 거짓 경보를 띄우게 된다.
 *
 * 그래서 두 벌이 **의도적으로 다르고**, 화면 쪽은 이 파일 하나만 쓴다 — 규칙이 화면 안에서
 * 두 벌이 되면 한쪽만 고쳐진다.
 *
 * 결정: `docs/decisions/2026-08-27-unresolved-approval-slot-park-and-resume.md`
 */
import { PARKED_SLOT_SIGNER_NAME } from '@safetyhub/shared';

/**
 * 대기 칸의 이름 자리에 서버가 넣는 값 — **정본은 `@safetyhub/shared`의
 * `PARKED_SLOT_SIGNER_NAME`**이고 server·web이 그 한 벌을 같이 쓴다(2026-08-30 수렴 —
 * 종전엔 server 유틸·server 쿼리 헬퍼·이 파일 셋에 같은 리터럴이 흩어져 있었다).
 * 이름은 화면 어휘에 맞춰 여기서 별칭으로만 둔다.
 */
export const PARKED_SIGNER_PLACEHOLDER: string = PARKED_SLOT_SIGNER_NAME;

/**
 * 대기 칸을 **사람 이름 자리**에 표시할 때 쓰는 문구(S39-b — 결재 이력·진행 배지).
 * 서버 플레이스홀더 `(담당자)`를 그대로 보이면 사람 이름처럼 읽힌다. 어휘는 대기 배너
 * (`parkedApprovalWaitingCopy` — "배정이 없어 … 대기 중")와 같은 축이다 — "미지정"은 이
 * 코드베이스에서 *종이 결재 칸*(빈 체인, S8·S67)을 가리키므로 여기 쓰지 않는다.
 */
export const PARKED_SLOT_DISPLAY_NAME = '담당자 배정 대기';

export interface ParkedCheckSignature {
  status: string;
  signerName?: string | null;
  memberId?: string | null;
}

/**
 * 이 서명행이 **담당자 없이 멈춘 칸**인가.
 *
 * ⚠️ **신원 FK 부재만으로는 부족하다.** dev 실측(2026-08-27): 신원 FK가 없는 `PENDING`
 * 행이 6건 있었는데 전부 **실제 사람 이름**(김남해·김대표·김소장)이었고 플레이스홀더는 0건이다.
 * 이들은 신원 FK 도입 전에 만들어진 **레거시 행**이지 멈춘 칸이 아니다 — 구 문서(46건)는
 * memberId를 소급해 채우지 않았고, 그 결재자는 토큰 경로(외부 결재 링크)로 여전히 결재한다
 * (구 프로필 성명 축 `profileNames.has(signerName)`은 2026-09-16 E4에서 엔티티째 사라졌다 —
 * 결재함 신원은 `member.userId` 하나다).
 * FK 부재만 보면 **정상 결재를 "담당자 지정 필요"로 오탐**한다 — 방향이 반대인 거짓 경보다.
 *
 * 그래서 이름 자리까지 본다. 대기 칸은 서버가 **반드시** 이름을 플레이스홀더로 덮으므로
 * (`parkApprovalSlotsForDepartedApprover`의 `signerName: PARKED_SLOT_SIGNER_NAME`,
 * 제출 시 `input.memberName ?? PARKED_SLOT_SIGNER_NAME`) 이 조합이 곧 "만들어지는 형상"이다.
 */
export function isApprovalSignatureParked(sig: ParkedCheckSignature): boolean {
  return (
    sig.status === 'PENDING' &&
    !sig.memberId &&
    sig.signerName === PARKED_SIGNER_PLACEHOLDER
  );
}

/**
 * **이 칸을 쥔 사람의 이름** — 결재 진행 배지(`deriveApprovalProgress.activeSignerName`)와 결재 이력
 * (`ApprovalHistorySection` `actorName`)이 같은 사다리를 읽는 한 벌(2026-08-30 리뷰 W-reuse8 —
 * 종전엔 두 자리가 각자 폴백을 적어 배지는 `signerName`만 보고 이력은 `member.name`까지 봤다).
 *
 * 사다리: 대기 칸이면 **null**(플레이스홀더 `(담당자)`를 사람 이름처럼 보이지 않게 — 대기 문구는
 * 호출부가 `PARKED_SLOT_DISPLAY_NAME`으로 따로 말한다) → `signerName`(공백 제거) → `member.name`
 * (서명 스냅샷 이름이 비었을 때의 신원 폴백) → null. 단계 라벨(`stepLabel`)은 **다른 축**이라
 * 여기서 폴백하지 않는다 — 라벨을 이름 자리에 넣으면 「안전관리자」가 사람 이름처럼 읽힌다.
 */
export function approvalSlotActorName(
  sig: ParkedCheckSignature & { member?: { name?: string | null } | null },
): string | null {
  if (isApprovalSignatureParked(sig)) return null;
  return sig.signerName?.trim() || sig.member?.name?.trim() || null;
}

/**
 * 대기 중인 칸 중 **가장 앞선 level** — 없으면 null.
 *
 * 배열 순서가 level 순이라는 보장이 없어 정렬해서 고른다(정의 배열 순서를 따라오는 경로가 있다).
 */
export function findParkedSignatureLevel<T extends ParkedCheckSignature & { approvalLevel: number }>(
  signatures: T[],
): number | null {
  const parked = [...signatures]
    .sort((a, b) => a.approvalLevel - b.approvalLevel)
    .find(isApprovalSignatureParked);
  return parked ? parked.approvalLevel : null;
}
